import React, { useState } from 'react';
import { Search, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useLanguage } from '../i18n/LanguageContext';
import { productCategories } from '../mock-products';

// Brand logos for the marquee
const brandLogos = [
  { name: 'Kitz', logo: '/assets/brands/kitz.png' },
  { name: '317', logo: '/assets/brands/317.png' },
  { name: 'Ariarmaturen', logo: '/assets/brands/ari.png' },
  { name: 'Avian', logo: '/assets/brands/avian.png' },
  { name: 'Benteler', logo: '/assets/brands/benteler.png' },
  { name: 'Bakrie', logo: '/assets/brands/bpi.png' },
  { name: 'Gunung Garuda', logo: '/assets/brands/gg.png' },
  { name: 'Gunung Raja Paksi', logo: '/assets/brands/grp.png' },
  { name: 'Ispatindo', logo: '/assets/brands/ibb.png' },
  { name: 'ISTW', logo: '/assets/brands/istw.png' },
  { name: 'Krakatau Steel', logo: '/assets/brands/ks.png' },
  { name: 'Maspion', logo: '/assets/brands/maspion.png' },
  { name: 'NDV', logo: '/assets/brands/ndv.png' },
  { name: 'Onda', logo: '/assets/brands/onda.png' },
  { name: 'KS-Posco', logo: '/assets/brands/posco.png' },
  { name: 'Rhomberg', logo: '/assets/brands/rhomberg.png' },
  { name: 'Rucika', logo: '/assets/brands/rucika.png' },
  { name: 'Sitong', logo: '/assets/brands/sitong.png' },
  { name: 'Stark Valve', logo: '/assets/brands/stark.png' },
  { name: 'Thermocel', logo: '/assets/brands/thermocel.png' },
  { name: 'Tozen', logo: '/assets/brands/tozen.png' },
  { name: 'Uni-D', logo: '/assets/brands/unid.png' },
  { name: 'Unilon', logo: '/assets/brands/unilon.png' },
  { name: 'Yuta', logo: '/assets/brands/yuta.png' },
  { name: 'Spindo', logo: 'https://lirp.cdn-website.com/2f73b385/dms3rep/multi/opt/SPINDO+MAIN+LOGO-1920w.png' },
];

const Products = () => {
  const { t, language } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');

  // Filter categories based on search term
  const filteredCategories = productCategories.filter(category => {
    const name = language === 'id' ? category.nameId : category.name;
    const description = language === 'id' ? category.descriptionId : category.description;
    return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      description.toLowerCase().includes(searchTerm.toLowerCase());
  });

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 via-white to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              {t('products.pageTitle')}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {t('products.pageSubtitle')}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <Input
                type="text"
                placeholder={t('products.searchPlaceholder')}
                className="pl-12 h-14 text-lg"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredCategories.map((category) => {
              const categoryName = language === 'id' ? category.nameId : category.name;
              const categoryDesc = language === 'id' ? category.descriptionId : category.description;
              const subCategoryCount = category.subCategories?.length || 0;

              return (
                <Link key={category.id} to={`/products/${category.slug}`}>
                  <Card className="group overflow-hidden hover:shadow-2xl transition-all duration-500 border-0 cursor-pointer h-full">
                    <div className="relative h-72 overflow-hidden">
                      <img
                        src={category.image}
                        alt={categoryName}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent group-hover:from-black/95 transition-all duration-500" />

                      {/* Product Count Badge */}
                      <div className="absolute top-4 right-4 bg-white/95 px-3 py-1.5 rounded-full">
                        <p className="text-xs font-bold text-[#0693E3]">
                          {subCategoryCount} {language === 'id' ? 'Sub-kategori' : 'Sub-categories'}
                        </p>
                      </div>

                      <div className="absolute bottom-6 left-6 right-6">
                        <h2 className="text-2xl font-bold text-white mb-2 group-hover:translate-x-2 transition-transform duration-300">
                          {categoryName}
                        </h2>
                        <p className="text-white/90 text-sm mb-4 group-hover:text-white transition-colors line-clamp-2">
                          {categoryDesc}
                        </p>

                        <Button
                          size="sm"
                          className="bg-[#0693E3] hover:bg-[#0578BE] text-white group-hover:scale-105 transition-transform"
                        >
                          {language === 'id' ? 'Lihat Produk' : 'View Products'}
                          <ArrowRight className="ml-2" size={16} />
                        </Button>
                      </div>

                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-[#0693E3]/0 group-hover:bg-[#0693E3]/10 transition-all duration-500" />
                    </div>
                  </Card>
                </Link>
              );
            })}
          </div>

          {filteredCategories.length === 0 && (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">{t('products.noResults')}</p>
            </div>
          )}
        </div>
      </section>

      {/* Weight Tables Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'id' ? 'Tabel Berat Material' : 'Material Weight Tables'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'id'
                ? 'Akses tabel berat untuk berbagai jenis material baja dan pipa'
                : 'Access weight tables for various steel and pipe materials'}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
            <Link to="/weight-table/pipa">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">

                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/pipe.png"
                    alt="Steel Pipe"
                    className="w-10 h-10 object-contain"
                  />
                </div>

                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Pipa Besi' : 'Steel Pipe'}
                </h3>

                <p className="text-xs text-gray-600">Weight Table</p>

              </Card>
            </Link>

            <Link to="/weight-table/cnp">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/cnp.png"
                    alt="CNP Channel"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Kanal CNP' : 'CNP Channel'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/unp">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/unp.png"
                    alt="UNP Channel"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Kanal UNP' : 'UNP Channel'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/hbeam">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/hbeam.png"
                    alt="H-Beam"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">H-Beam</h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/inp">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/hbeam.png"
                    alt="INP"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'INP / I-Beam' : 'INP / I-Beam'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/iwf">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/hbeam.png"
                    alt="IWF"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'IWF / Wide Flange' : 'IWF / Wide Flange'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/pipa-kotak">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/square.png"
                    alt="Box Pipe"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Pipa Kotak' : 'Box Pipe'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/pipa-boiler">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/boiler.png"
                    alt="Boiler Pipe"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Pipa Boiler' : 'Boiler Pipe'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/assental">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/as.png"
                    alt="Round Bar"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Besi As' : 'Round Bar'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>

            <Link to="/weight-table/besi-siku">
              <Card className="p-6 text-center hover:shadow-xl transition-all hover:-translate-y-2 duration-300 cursor-pointer border-2 border-transparent hover:border-[#0693E3]">
                <div className="mb-3 flex justify-center">
                  <img
                    src="/assets/icons/angle.png"
                    alt="Round Bar"
                    className="w-10 h-10 object-contain"
                  />
                </div>
                <h3 className="font-bold text-gray-900 text-sm mb-1">
                  {language === 'id' ? 'Besi SIku' : 'Angle Bar'}
                </h3>
                <p className="text-xs text-gray-600">Weight Table</p>
              </Card>
            </Link>


          </div>
        </div>
      </section>

      {/* Trusted Brands Marquee Section */}
      <section className="py-16 bg-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              {language === 'id' ? 'Merek Terpercaya' : 'Trusted Brands'}
            </h2>
            <p className="text-gray-600">
              {language === 'id'
                ? 'Kami menyediakan produk dari merek-merek ternama di industri'
                : 'We provide products from leading brands in the industry'}
            </p>
          </div>
        </div>

        {/* Marquee Container */}
        <div className="relative">
          {/* Gradient overlays */}
          <div className="absolute left-0 top-0 bottom-0 w-32 bg-gradient-to-r from-white to-transparent z-10" />
          <div className="absolute right-0 top-0 bottom-0 w-32 bg-gradient-to-l from-white to-transparent z-10" />

          {/* Scrolling logos */}
          <div className="flex w-max animate-marquee-brands">
            {[...brandLogos, ...brandLogos].map((brand, index) => (
              <div
                key={index}
                className="flex-shrink-0 mx-8 flex items-center justify-center"
                style={{ minWidth: '150px', height: '80px' }}
              >
                <img
                  src={brand.logo}
                  alt={brand.name}
                  className="max-h-12 max-w-[130px] object-contain filter grayscale hover:grayscale-0 transition-all duration-300 opacity-60 hover:opacity-100"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentElement.innerHTML = `<span class="text-gray-500 font-bold text-lg">${brand.name}</span>`;
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        <style>{`
          @keyframes marquee-brands {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee-brands {
            animation: marquee-brands 30s linear infinite;
          }
          .animate-marquee-brands:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">{t('products.ctaTitle')}</h2>
          <p className="text-xl text-gray-400 mb-8 max-w-2xl mx-auto">
            {t('products.ctaSubtitle')}
          </p>
          <Link to="/contact">
            <Button size="lg" className="bg-[#CF2E2E] hover:bg-[#B02525] text-white">
              {t('products.contactTeam')}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Products;